import { apiClient } from '@/services/api-client';
import type {
  CustomerInvoicesListParams,
  CustomerInvoicesMeta,
  CustomerInvoicesPage,
  CustomerInvoiceStatus,
} from '@/types/customer-invoices';

type UnknownRecord = Record<string, unknown>;

const readRecord = (value: unknown): UnknownRecord =>
  value && typeof value === 'object' && !Array.isArray(value) ? (value as UnknownRecord) : {};

const readString = (value: unknown): string | null => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim();
  return trimmed.length ? trimmed : null;
};

const readNumber = (value: unknown): number => {
  if (typeof value === 'number' && Number.isFinite(value)) return value;
  if (typeof value === 'string') {
    const parsed = Number(value);
    if (Number.isFinite(parsed)) return parsed;
  }
  return 0;
};

const normalizeStatus = (value: unknown): CustomerInvoiceStatus => {
  const status = readString(value)?.toLowerCase();
  switch (status) {
    case 'paid':
    case 'open':
    case 'draft':
    case 'void':
    case 'uncollectible':
      return status;
    default:
      return 'open';
  }
};

const normalizeMeta = (input: unknown, fallbackPerPage: number): CustomerInvoicesMeta => {
  const record = readRecord(input);
  return {
    perPage: Math.max(1, Math.min(100, readNumber(record.per_page ?? record.perPage) || fallbackPerPage)),
    nextCursor: readString(record.next_cursor ?? record.nextCursor),
    prevCursor: readString(record.prev_cursor ?? record.prevCursor),
    hasMore: Boolean(record.has_more ?? record.hasMore),
  };
};

const normalizeInvoicesPage = (
  invoicesInput: unknown,
  metaInput: unknown,
  limit: number,
): CustomerInvoicesPage => {
  const list = Array.isArray(invoicesInput) ? invoicesInput : [];
  const meta = normalizeMeta(metaInput, limit);

  return {
    invoices: list.map((entry) => {
      const record = readRecord(entry);
      const currency = readString(record.currency) ?? 'USD';
      return {
        id: readString(record.id) ?? '',
        number: readString(record.number),
        amountDue: readNumber(record.amount_due ?? record.amountDue),
        amountPaid: readNumber(record.amount_paid ?? record.amountPaid),
        amountRemaining: readNumber(record.amount_remaining ?? record.amountRemaining),
        currency,
        status: normalizeStatus(record.status),
        companyName: readString(record.company_name ?? record.companyName),
        issuedAt: readString(record.issued_at ?? record.issuedAt),
        paidAt: readString(record.paid_at ?? record.paidAt),
        billingPeriodStart: readString(record.billing_period_start ?? record.billingPeriodStart),
        billingPeriodEnd: readString(record.billing_period_end ?? record.billingPeriodEnd),
        hostedInvoiceUrl: readString(record.hosted_invoice_url ?? record.hostedInvoiceUrl),
        downloadPdfUrl: readString(record.download_pdf_url ?? record.downloadPdfUrl),
        invoicePdf: readString(record.invoice_pdf ?? record.invoicePdf),
      };
    }),
    meta,
  };
};

class CustomerInvoicesService {
  async listInvoices(params: CustomerInvoicesListParams = {}): Promise<CustomerInvoicesPage> {
    const limit = Math.max(1, Math.min(100, params.limit ?? 20));
    const cursor = params.cursor ?? null;
    const response = await apiClient.get<unknown>('/api/v1/customer/invoices', {
      params: {
        limit,
        ...(cursor ? { cursor } : {}),
      },
    });

    // LiveAdapter flattens non-JSON:API responses to `response.data.data` when present,
    // exposing pagination meta separately on `response.meta`.
    // MockAdapter returns the raw object.
    const root = readRecord(response.data);
    const invoices = Array.isArray(response.data) ? response.data : root.data;
    const meta = response.meta ?? root.meta;

    return normalizeInvoicesPage(invoices, meta, limit);
  }
}

export const customerInvoicesService = new CustomerInvoicesService();
